import React, { useState } from 'react';
import { AgUiMessage } from '../../types/agui';
import { Copy, Check, Download, Bot, User, ShieldAlert } from 'lucide-react';

interface MessageBubbleProps {
  message: AgUiMessage;
}

export const MessageBubble: React.FC<MessageBubbleProps> = React.memo(({ message }) => {
  const [copied, setCopied] = useState(false);
  const isUser = message.role === 'user';

  const copyToClipboard = () => {
    navigator.clipboard.writeText(message.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadMarkdown = () => {
    const filename = `finance_analysis_${message.id}.md`;
    const blob = new Blob([message.content], { type: 'text/markdown;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Safe and clean markdown processor
  const renderFormattedContent = (content: string) => {
    if (!content) return null;

    // Detect regulatory disclaimer block
    const hasDisclaimer = content.includes('DISCLAIMER') || content.includes('Regulatory Disclaimer');

    // Split paragraphs
    const paragraphs = content.split('\n\n');

    return (
      <div className="markdown-render">
        {paragraphs.map((p, idx) => {
          const trimmed = p.trim();
          if (!trimmed) return null;

          // Headers
          if (trimmed.startsWith('### ')) {
            return <h4 key={idx}>{trimmed.substring(4)}</h4>;
          }
          if (trimmed.startsWith('## ')) {
            return <h3 key={idx}>{trimmed.substring(3)}</h3>;
          }
          if (trimmed.startsWith('# ')) {
            return <h2 key={idx}>{trimmed.substring(2)}</h2>;
          }

          // Bullet lists
          if (trimmed.startsWith('- ') || trimmed.startsWith('* ')) {
            const items = trimmed.split('\n').filter((l) => l.trim().length > 0);
            return (
              <ul key={idx}>
                {items.map((item, itemIdx) => {
                  const cleaned = item.replace(/^[-*]\s+/, '');
                  return <li key={itemIdx} dangerouslySetInnerHTML={{ __html: formatInline(cleaned) }} />;
                })}
              </ul>
            );
          }

          // Disclaimer box
          if (hasDisclaimer && (trimmed.includes('DISCLAIMER') || trimmed.includes('educational purposes only'))) {
            return (
              <div key={idx} className="disclaimer-callout">
                <ShieldAlert size={16} className="disclaimer-icon" />
                <div dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }} />
              </div>
            );
          }

          // Markdown Table
          if (trimmed.startsWith('|') && trimmed.includes('\n|')) {
            const tableLines = trimmed.split('\n').map((l) => l.trim()).filter((l) => l.startsWith('|') && l.endsWith('|'));
            if (tableLines.length >= 2) {
              const parseRow = (line: string) =>
                line.split('|').slice(1, -1).map((cell) => cell.trim());
              const headerCells = parseRow(tableLines[0]);
              const isSeparator = /^\|?(\s*:?-+:?\s*\|?)+$/.test(tableLines[1]);
              const bodyLines = isSeparator ? tableLines.slice(2) : tableLines.slice(1);

              return (
                <div key={idx} className="table-responsive-wrapper">
                  <table className="financial-data-table">
                    <thead>
                      <tr>
                        {headerCells.map((h, hIdx) => (
                          <th key={hIdx} dangerouslySetInnerHTML={{ __html: formatInline(h) }} />
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {bodyLines.map((bLine, rIdx) => {
                        const cells = parseRow(bLine);
                        return (
                          <tr key={rIdx}>
                            {cells.map((cell, cIdx) => (
                              <td key={cIdx} dangerouslySetInnerHTML={{ __html: formatInline(cell) }} />
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            }
          }

          // Code block
          if (trimmed.startsWith('```')) {
            const codeContent = trimmed.replace(/^```[a-z]*\n?/, '').replace(/```$/, '');
            return (
              <pre key={idx} className="code-block">
                <code>{codeContent}</code>
              </pre>
            );
          }

          return <p key={idx} dangerouslySetInnerHTML={{ __html: formatInline(trimmed) }} />;
        })}
      </div>
    );
  };

  const escapeHtml = (str: string) => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  };

  const formatInline = (text: string) => {
    const safeText = escapeHtml(text);
    return safeText
      .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/g, '<em>$1</em>')
      .replace(/`([^`]+)`/g, '<code class="inline-code">$1</code>');
  };

  return (
    <div className={`message-row ${isUser ? 'user-row' : 'assistant-row'}`}>
      <div className="message-avatar">
        {isUser ? <User size={18} /> : <Bot size={18} className="bot-icon" />}
      </div>

      <div className="message-bubble-wrapper">
        <div className="message-header-meta">
          <span className="message-author">{isUser ? 'You' : 'Finance Advisor Agent'}</span>
          {message.timestamp && <span className="message-timestamp">{message.timestamp}</span>}
          {!isUser && message.content && (
            <div className="message-actions">
              <button onClick={copyToClipboard} className="msg-action-btn" title="Copy to clipboard">
                {copied ? <Check size={13} className="text-green" /> : <Copy size={13} />}
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
              <button onClick={downloadMarkdown} className="msg-action-btn" title="Download report (.md)">
                <Download size={13} />
                <span>Export</span>
              </button>
            </div>
          )}
        </div>

        <div className={`message-bubble ${isUser ? 'bubble-user' : 'bubble-assistant'}`}>
          {renderFormattedContent(message.content)}
        </div>
      </div>
    </div>
  );
});
